import { flow, isEmpty, isEqual } from 'lodash';
import { createSelector } from 'reselect';

import { NAME } from '~/constants/login';
import {
  CORVA_COMPANY_PROVIDER,
  EQUINOR_EU_COMPANY_PROVIDER,
  EQUINOR_PROD_COMPANY_PROVIDER,
  HAL_COMPANY_PROVIDER,
} from '~/constants/main';
import { checkIsNonEmptySingleAsset, getSingleAssetObjectFromUrlSearch } from '~/utils/singleAsset';

const stateSelector = state => state[NAME];

export const currentUserSelector = flow(stateSelector, state => state.get('currentUser'));
export const loginAuthFailureSelector = flow(stateSelector, state => state.get('loginAuthFailure'));
export const loggingInSelector = flow(stateSelector, state => state.get('loggingIn'));
export const loginApiFailureSelector = flow(stateSelector, state => state.get('loginApiFailure'));
export const loginTermsFailureSelector = flow(stateSelector, state =>
  state.get('loginTermsFailure')
);
export const loginAccountLockedFailureSelector = flow(stateSelector, state =>
  state.get('loginAccountLockedFailure')
);
export const invalidVerificationCodeSelector = flow(stateSelector, state =>
  state.get('invalidVerificationCode')
);
export const missingVerificationCodeSelector = flow(stateSelector, state =>
  state.get('missingVerificationCode')
);
export const loginFailureSelector = state =>
  loginAuthFailureSelector(state) ||
  loginApiFailureSelector(state) ||
  loginTermsFailureSelector(state) ||
  loginAccountLockedFailureSelector(state) ||
  invalidVerificationCodeSelector(state) ||
  missingVerificationCodeSelector(state);

export const isCurrentUserAdminSelector = flow(stateSelector, state => {
  const role = state.getIn(['currentUser', 'role']);
  return role === 'admin' || role === 'developeradmin';
});

export const isCurrentUserCorvaUserSelector = flow(
  stateSelector,
  state => state.getIn(['currentUser', 'company', 'provider'], '') === CORVA_COMPANY_PROVIDER
);

export const isCurrentUserHalliburtonUserSelector = flow(
  stateSelector,
  state => state.getIn(['currentUser', 'company', 'provider'], '') === HAL_COMPANY_PROVIDER
);

export const isCurrentUserEquinorUserSelector = flow(stateSelector, state => {
  const provider = state.getIn(['currentUser', 'company', 'provider'], '');

  return provider === EQUINOR_PROD_COMPANY_PROVIDER || provider === EQUINOR_EU_COMPANY_PROVIDER;
});

export const isImpersonatingSelector = createSelector([currentUserSelector], currentUser => {
  return currentUser?.get('impersonating') ?? false;
});

export const getCurrentUser = createSelector(
  [currentUserSelector],
  currentUser => currentUser && currentUser.toJS()
);

export const currentUserCompanyId = createSelector([currentUserSelector], currentUser =>
  currentUser.getIn(['company', 'id'])
);

export const currentUserCompanyProviderSelector = createSelector(
  [currentUserSelector],
  currentUser => currentUser.getIn(['company', 'provider'])
);

export const isCorvaV3FeaturesEnabledSelector = createSelector([currentUserSelector], currentUser =>
  currentUser?.getIn(['settings', 'uiSettings', 'corva_v3_features_enabled'], false)
);

export const isInterventionsEnabledSelector = createSelector([currentUserSelector], currentUser =>
  currentUser?.getIn(['settings', 'uiSettings', 'interventions'], false)
);

export const corvaV3Selector = createSelector([getCurrentUser], currentUser => {
  if (!currentUser?.settings?.uiSettings) {
    return {
      isEnabled: false,
      isInUse: false,
      skipClickTimestamp: null,
    };
  }
  const { uiSettings } = currentUser.settings;
  const isEnabled = uiSettings.corva_v3_features_enabled ?? false;
  const isInUse = uiSettings.corva_v3_features_in_use ?? false;
  const skipClickTimestamp = uiSettings.corva_v3_features_skip_timestamp ?? null;
  return {
    isEnabled,
    isInUse,
    skipClickTimestamp,
  };
});

export const currentUserSingleAssetSelectorOrigin = createSelector(
  [currentUserSelector],
  currentUser => currentUser.getIn(['settings', 'singleAsset'])?.toJS() || {}
);

const SINGLE_ASSET_SESSION_STORAGE_KEY = 'single_asset';

const getParsedSingleAssetFormSessionStorage = () => {
  const stringValueFromStorage = sessionStorage.getItem(SINGLE_ASSET_SESSION_STORAGE_KEY) || '{}';

  let result = {};
  try {
    result = JSON.parse(stringValueFromStorage);
  } catch (e) {
    console.error(e);
  }

  return result;
};

const setSingleAssetToSessionStorage = (singleAsset = {}) => {
  const stringifiedValue = JSON.stringify(singleAsset);
  try {
    sessionStorage.setItem(SINGLE_ASSET_SESSION_STORAGE_KEY, stringifiedValue);
  } catch (e) {
    console.error(e);
  }
};

// NOTE: we need to use single asset from URL
// right now there a lot of places where are this selector is used
// remove it and pass single asset via DashboardInfoStore context
let lastSearch = '';
// for page reload we save single asset in session storage
let lastResult = getParsedSingleAssetFormSessionStorage();
// let lastResult = {};

// TODO: rename
export const currentUserSingleAssetSelector = createSelector(
  [currentUserSingleAssetSelectorOrigin, () => window.location.search],
  (currentUserSingleAsset, search) => {
    const shouldReturnCachedResult = search === lastSearch && !isEmpty(lastResult);
    if (shouldReturnCachedResult) {
      return lastResult;
    }

    const singleAssetFromUrl = getSingleAssetObjectFromUrlSearch(search);
    const isSingleAssetFromUrlNonEmpty = checkIsNonEmptySingleAsset(singleAssetFromUrl);
    const singleAssetWithUrlOverwrites = {
      ...currentUserSingleAsset, // Saved on BE in user settings
      ...lastResult, // cached in session storage
      ...(isSingleAssetFromUrlNonEmpty && singleAssetFromUrl), // URL overrides
    };

    if (!isEqual(singleAssetWithUrlOverwrites, lastResult)) {
      lastResult = singleAssetWithUrlOverwrites;
      setSingleAssetToSessionStorage(lastResult);
      lastSearch = search;
    }

    // return cached result - the same object each time if nothing was changed
    return lastResult;
  }
);
